import { request, requestWithMsg, uploadFileWithMsg, MsgResult, PageResult } from './request';
import { DeleteCascadePayload, fetchDeletePreview, RelatedGroup } from './cascade';
import { DeviceExtValue, SchemaGroup } from './schema';

/**
 * capability 组能力值 (2026-08-14 store=column 机制移除: 光源/功率/光斑等不再是设备顶层字段,
 * 统一存 device_ext_value 的 capability 组; 列表/详情接口随行附带本对象).
 * 键 = device_schema 字段 key; 未配置的键不存在.
 */
export interface DeviceCapability {
    source?: string;
    band?: string;
    stdW?: number;
    minStableW?: number;
    minStablePct?: number;
    spotXMm?: number;
    spotYMm?: number;
    onDelayMs?: number;
    speedMinMmS?: number;
    speedMaxMmS?: number;
    /** 禁速带 JSON 文本 (形如 [[lo,hi],...]) */
    forbiddenSpeedBands?: string;
    /** 支持夹具 (数组; 历史数据可能是 JSON 文本) */
    supportedFixtures?: string[] | string;
    [key: string]: unknown;
}

export interface Device {
    id: number;
    uuid: string;
    deviceKey: string;
    /** 手动排序号 (设备管理 tab 维护, 升序; 0=未设置排最后) */
    sno?: number;
    /** 设备名称 (与源数据 xcs devices.name 一致; 展示用, deviceKey 仍是业务主键) */
    name?: string;
    internalKey?: string;
    family: string;
    archCode: string;
    /** capability 组能力值 (列表/详情接口附带; 写入走 upsertDeviceExtValue) */
    capability?: DeviceCapability;
    /** 绑定的换算服务 (工具市场 toolKey, 统一 Tool API 调用标识); 空 = 未绑定. */
    convertToolKey?: string;
    status: string;
    /** 是否已发布 1 未发布 / 2 已发布 (2026-09-03; 常量见 components/PublishBadge): 只约束 OpenAPI 对外下发 (设备本身 + 挂在它上的参数行), 平台内不受限 */
    isPublished: number;
    /** 最近一次发布时间 (unix 秒, 0 = 从未发布; 取消发布不清零) */
    publishedAt?: number;
    createdBy: number;
    updatedBy: number;
    remark?: string;
    createdAt?: string;
    updatedAt?: string;
}

/** 设备展示名: 源数据名称 + (光源 功率) 变体后缀, 无名称回退 deviceKey. deviceKey 仍是业务主键, 仅展示层用.
 * lightText: 光源 code → 字典 label (useDictText('light_source')); 不传回退裸 code (纯函数拿不到 hook, 由调用方注入). */
export function deviceLabel(
    d: Pick<Device, 'name' | 'deviceKey' | 'capability'>,
    lightText?: (code?: string) => string,
): string {
    const cap = d.capability;
    const src = cap?.source ? (lightText ? lightText(cap.source) : cap.source) : '';
    const variant = [src, cap?.stdW ? `${cap.stdW}W` : ''].filter(Boolean).join(' ');
    return `${d.name || d.deviceKey}${variant ? ` (${variant})` : ''}`;
}

export interface DeviceMode {
    deviceKey: string;
    mode: string;
    isEnabled: boolean;
    speedMin?: number;
    speedMax?: number;
    note?: string;
}

export interface DeviceCalibration {
    deviceKey: string;
    pct: number;
    realW: number;
}

export interface DeviceThermalCal {
    deviceKey: string;
    mode: string;
    materialType: string;
    tau?: number;
    beta?: number;
}

export interface DeviceDetail extends Device {
    modes: DeviceMode[];
    calibration: DeviceCalibration[];
    thermalCal: DeviceThermalCal[];
    /** YAML 原文 sha256 hash (导入后由后端写入, 去重 + 防篡改). */
    yamlHash?: string;
    /** YAML 原文 (导入后由后端写入, 'YAML 预览' tab 优先展示). */
    yamlRaw?: string;
    /** 本设备可见的动态字段组 (后端已按 archCode + 组内设备绑定过滤); 工况页各卡片共用, 免各自拉 device-schemas. */
    schemaGroups?: SchemaGroup[];
    /** 本设备各组已存动态字段值; 同上, 免各自拉 ext-values. */
    extValues?: DeviceExtValue[];
}

export interface ListDevicesParams {
    keyword?: string;
    archCode?: string;
    family?: string;
    status?: string;
    /** 发布状态筛选 1 未发布 / 2 已发布, 不传 = 全部 */
    isPublished?: number;
    page?: number;
    pageSize?: number;
}

const BASE = '/v1';

export function listDevices(params: ListDevicesParams): Promise<PageResult<Device>> {
    return request<PageResult<Device>>(`${BASE}/devices`, { query: params });
}

/** 后端单页上限 (device handler maxPageSize); 传更大的 pageSize 会被静默截断到这个值. */
const DEVICE_PAGE_MAX = 100;

/** 全量设备 (按 totalCount 逐页拉齐, 不受单页上限截断); 用于需要完整设备候选的多选场景. */
export async function listAllDevices(
    params: Omit<ListDevicesParams, 'page' | 'pageSize'> = {},
): Promise<Device[]> {
    const first = await listDevices({ ...params, page: 1, pageSize: DEVICE_PAGE_MAX });
    const all = [...(first.list || [])];
    const pages = Math.ceil((first.totalCount || 0) / DEVICE_PAGE_MAX);
    for (let page = 2; page <= pages; page++) {
        const res = await listDevices({ ...params, page, pageSize: DEVICE_PAGE_MAX });
        all.push(...(res.list || []));
    }
    return all;
}

export function getDevice(deviceKey: string): Promise<DeviceDetail> {
    return request<DeviceDetail>(`${BASE}/devices/detail`, { method: 'POST', dedupe: true, body: { deviceKey } });
}

// 设备写接口: 统一走审批流 (message 含"已提交审批", data={taskCode}).
export function createDevice(
    payload: Partial<Device> & { deviceKey: string; family: string; archCode: string },
): Promise<MsgResult<{ id?: number; deviceKey?: string; taskCode?: string }>> {
    return requestWithMsg(`${BASE}/devices`, { method: 'POST', body: payload });
}

export function updateDevice(
    deviceKey: string,
    payload: Partial<Device>,
): Promise<MsgResult<{ taskCode?: string } | null>> {
    return requestWithMsg(`${BASE}/devices/update`, { method: 'POST', body: { deviceKey, ...payload } });
}

/** 发布 / 取消发布 (2026-09-03): 只翻 isPublished (1→2, + 发布时刻记 publishedAt), 决定 OpenAPI 是否下发; 走审批. */
export function publishDevice(deviceKey: string, publish: boolean): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/devices/publish`, { method: 'POST', body: { deviceKey, publish } });
}

/** 删设备 (硬删). cascade 勾选见 fetchDeviceDeletePreview / DeleteCascadeModal. */
export function deleteDevice(
    deviceKey: string,
    payload?: DeleteCascadePayload,
): Promise<MsgResult<{ taskCode?: string } | null>> {
    // 业务键走 body (后端 deleteReq.deviceKey 必填); 级联勾选可缺省
    return requestWithMsg(`${BASE}/devices/delete`, {
        method: 'POST',
        body: { deviceKey, ...(payload || {}) },
    });
}

/** 删设备前的关联数据预检 (弹窗勾选用). */
export function fetchDeviceDeletePreview(deviceKey: string): Promise<RelatedGroup[]> {
    return fetchDeletePreview(`${BASE}/devices/delete-preview`, { deviceKey });
}

/** 模式能力开关输入行 (PUT /devices/:key/modes 整组覆盖). */
export interface DeviceModeInput {
    mode: string;
    isEnabled: boolean;
    speedMin?: number;
    speedMax?: number;
    note?: string;
}

/** 页面级编辑模式能力开关 (整组覆盖). 统一走审批工单. */
export function updateDeviceModes(
    deviceKey: string,
    modes: DeviceModeInput[],
): Promise<MsgResult<{ count?: number; taskCode?: string } | null>> {
    // 业务键走 body (2026-08-24 起后端 updateModesReq.deviceKey 必填)
    return requestWithMsg(`${BASE}/devices/modes/update`, {
        method: 'POST',
        body: { deviceKey, modes },
    });
}

/** 设备手动排序 (仅管理员): 传完整有序 deviceKey 列表, 后端依序重写 sno=1..N. */
export function reorderDevices(deviceKeys: string[]): Promise<MsgResult<{ count: number }>> {
    return requestWithMsg(`${BASE}/devices-reorder`, { method: 'PUT', body: { deviceKeys } });
}

/**
 * YAML 导入设备工况. multipart/form-data, 后端解析后落 device + 子表.
 * 统一走审批工单 (message 含"已提交审批", data={taskCode}).
 */
export function importDeviceYaml(
    file: File,
): Promise<MsgResult<{ deviceKey?: string; taskCode?: string; warnings?: string[] }>> {
    return uploadFileWithMsg(`${BASE}/devices/yaml/import`, file);
}

// ---- 功率标定单独导入 (xlsx / yaml, 2026-09-15) ---------------------------------------
// 与 PUT calibration 同语义: 按 device_key 整曲线覆盖 (先清空再写入); 目标设备在页面选择.

/** 功率标定导入模板下载路径 (GET; 必须走 downloadFile 带 utoken). */
export const CALIB_IMPORT_TEMPLATE_PATH = `${BASE}/devices/calibration/import-template`;

/** dryRun 预检 / 正式导入共用的返回体. */
export interface CalibImportPreview {
    deviceKey: string;
    /** 解析后的点 (pct 升序) */
    points: { pct: number; realW: number }[];
    count: number;
    /** 设备当前已有点数; >0 表示本次导入是覆盖 */
    existingCount: number;
    overwrite: boolean;
    /** 软告警 (非单调 / 缺端点 / 超标准功率), 不拦截 */
    warnings: string[];
    /** 走审批流时的工单号 */
    taskCode?: string;
}

/**
 * 导入功率标定曲线 (xlsx 模板 / 设备 YAML 的 power_calibration 段). dryRun=true 只解析+校验+回当前点数, 不写库不建工单;
 * 正式导入统一走审批工单 (message 含"已提交审批", data={taskCode}); 本地 BYPASS 直接落库.
 */
export function importDeviceCalibration(
    file: File,
    deviceKey: string,
    dryRun = false,
): Promise<MsgResult<CalibImportPreview>> {
    return uploadFileWithMsg(`${BASE}/devices/calibration/import`, file, 'file', {
        deviceKey,
        ...(dryRun ? { dryRun: 'true' } : {}),
    });
}
